/**
 * Browser relock used by the live tracker and worker.
 *
 * Uses the same high-quality path as headless:
 *   - xfeat_learn_steer_rgb01.onnx, [1,3,H,W] BGR01 input
 *   - prepareBgr01ForNet() floor-multiple-of-32 resize (1200 -> 1184)
 *   - learned steeredMatch()
 *   - bestHomography(... method:'magsac', threshs:[3])
 *   - render scale search by shrinking render inside the padded square
 *
 * This file intentionally keeps the old RelockEngine public API so the worker
 * architecture stays intact while matching headless behavior much more closely.
 */
import { steeredMatch, bestHomography, applyLearnedSteer, applyPermSteer } from "./xfeat.js";
import { prepareBgr01ForNet, detectFromNetOutputs } from "./xfeat_preprocess.js";
import { createSession } from "./ort.js";
import { flat3, mat3Mul, mat3Inv, scaleInsideMatrix } from "./homography.js";
import { canonicalCanvasFromFrame, transformRelockToFrame } from "./relock_canonical.js";
import { GpuMnnMatcher } from "./gpu_mnn.js";

const RENDER_SIZE = 1200;

function perfNow() { return globalThis.performance?.now ? performance.now() : Date.now(); }

function postDiag(msg) {
  try { if (typeof self !== "undefined" && self.postMessage) self.postMessage({ id: -1, kind: "diag", msg }); } catch {}
}

function createProcCanvas(w, h) {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

function mat3FromScalePad(natW, natH, size) {
  const sq = Math.max(natW, natH);
  const padX = (sq - natW) / 2;
  const padY = (sq - natH) / 2;
  const s = size / sq;
  return [s, 0, padX * s, 0, s, padY * s, 0, 0, 1];
}

function renderScaleMatrix(natW, natH, size, scaleInside) {
  return mat3Mul(scaleInsideMatrix(size, scaleInside), mat3FromScalePad(natW, natH, size));
}

/** Headless padToSquare + scaleInside equivalent, returning planar BGR01. */
export function preprocessSourceBgr01(img, size = RENDER_SIZE, scaleInside = 1.0) {
  const tTotal = perfNow();
  const timing = {};
  const natW = img.naturalWidth || img.videoWidth || img.width;
  const natH = img.naturalHeight || img.videoHeight || img.height;
  let t = perfNow();
  const canvas = createProcCanvas(size, size);
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  timing.canvas_ms = perfNow() - t;
  t = perfNow();
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, size, size);

  const sq = Math.max(natW, natH);
  const base = size / sq;
  const drawW = natW * base * scaleInside;
  const drawH = natH * base * scaleInside;
  const x0 = (size - drawW) / 2;
  const y0 = (size - drawH) / 2;
  ctx.drawImage(img, x0, y0, drawW, drawH);
  timing.draw_ms = perfNow() - t;

  t = perfNow();
  const { data } = ctx.getImageData(0, 0, size, size);
  timing.get_image_data_ms = perfNow() - t;
  t = perfNow();
  const bgr01 = new Float32Array(3 * size * size);
  const plane = size * size;
  for (let i = 0; i < plane; i++) {
    const r = data[i * 4], g = data[i * 4 + 1], b = data[i * 4 + 2];
    bgr01[i] = b / 255;
    bgr01[plane + i] = g / 255;
    bgr01[2 * plane + i] = r / 255;
  }
  timing.pack_bgr_ms = perfNow() - t;

  const M = renderScaleMatrix(natW, natH, size, scaleInside);
  timing.total_ms = perfNow() - tTotal;
  return { bgr01, W: size, H: size, canvas, M, scaleInside, natW, natH, timing };
}

function packCanvasBgr01(canvas, timing) {
  const w = canvas.width, h = canvas.height;
  const t0 = perfNow();
  const { data } = canvas.getContext("2d", { willReadFrequently: true }).getImageData(0, 0, w, h);
  timing.get_image_data_ms = perfNow() - t0;
  const t1 = perfNow();
  const bgr01 = new Float32Array(3 * w * h);
  const plane = w * h;
  for (let i = 0; i < plane; i++) {
    const r = data[i * 4], g = data[i * 4 + 1], b = data[i * 4 + 2];
    bgr01[i] = b / 255;
    bgr01[plane + i] = g / 255;
    bgr01[2 * plane + i] = r / 255;
  }
  timing.pack_bgr_ms = perfNow() - t1;
  return bgr01;
}

function subsetFeatureSet(det, indices, tile) {
  const n = indices.length;
  const kpts = new Float32Array(n * 2);
  const scores = new Float32Array(n);
  const descs = new Float32Array(n * 64);
  for (let outI = 0; outI < n; outI++) {
    const i = indices[outI];
    kpts[outI * 2] = det.kpts[i * 2] - tile.x;
    kpts[outI * 2 + 1] = det.kpts[i * 2 + 1] - tile.y;
    scores[outI] = det.scores[i];
    descs.set(det.descs.subarray(i * 64, i * 64 + 64), outI * 64);
  }
  return { kpts, scores, descs, n, W: tile.W, H: tile.H };
}

function splitAtlasFeatures(det, tiles, topK, guard = 0) {
  const buckets = tiles.map(() => []);
  for (let i = 0; i < det.n; i++) {
    const x = det.kpts[i * 2], y = det.kpts[i * 2 + 1];
    for (let ti = 0; ti < tiles.length; ti++) {
      const t = tiles[ti];
      if (x >= t.x + guard && x < t.x + t.W - guard && y >= t.y + guard && y < t.y + t.H - guard) {
        if (buckets[ti].length < topK) buckets[ti].push(i);
        break;
      }
    }
  }
  return tiles.map((tile, i) => subsetFeatureSet(det, buckets[i], tile));
}

function preprocessFrameAtlasBgr01(items, size = 640, { gutter = 96, cols = null } = {}) {
  const tTotal = perfNow();
  const timing = {};
  const n = items.length;
  const nCols = Math.max(1, cols || (n <= 4 ? n : Math.ceil(Math.sqrt(n))));
  const nRows = Math.ceil(n / nCols);
  const W = nCols * size + Math.max(0, nCols - 1) * gutter;
  const H = nRows * size + Math.max(0, nRows - 1) * gutter;
  let t = perfNow();
  const canvas = createProcCanvas(W, H);
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  timing.canvas_ms = perfNow() - t;
  t = perfNow();
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, W, H);
  const tiles = [];
  for (let i = 0; i < n; i++) {
    const source = items[i].source;
    const natW = source.naturalWidth || source.videoWidth || source.width;
    const natH = source.naturalHeight || source.videoHeight || source.height;
    const col = i % nCols;
    const row = Math.floor(i / nCols);
    const tileX = col * (size + gutter);
    const tileY = row * (size + gutter);
    const sq = Math.max(natW, natH);
    const base = size / sq;
    const drawW = natW * base;
    const drawH = natH * base;
    const x0 = tileX + (size - drawW) / 2;
    const y0 = tileY + (size - drawH) / 2;
    ctx.drawImage(source, x0, y0, drawW, drawH);
    tiles.push({ frame: items[i].frame, x: tileX, y: tileY, W: size, H: size, M: mat3FromScalePad(natW, natH, size), natW, natH });
  }
  timing.draw_ms = perfNow() - t;
  const bgr01 = packCanvasBgr01(canvas, timing);
  timing.total_ms = perfNow() - tTotal;
  return { bgr01, W, H, canvas, tiles, timing, gutter, size };
}

function projectWithH(H, pts) {
  const M = flat3(H);
  return pts.map(([x, y]) => {
    const z = M[6] * x + M[7] * y + M[8];
    return [(M[0] * x + M[1] * y + M[2]) / z, (M[3] * x + M[4] * y + M[5]) / z];
  });
}

function localScales(center, radius = 0.10) {
  const out = [];
  for (let s = Math.max(0.25, center - radius); s <= Math.min(1.0, center + radius + 1e-6); s += 0.05) {
    out.push(Math.round(s * 100) / 100);
  }
  return [...new Set(out)];
}

function fullScales() {
  return [0.25,0.30,0.35,0.40,0.45,0.50,0.55,0.60,0.65,0.70,0.75,0.80,0.85,0.90,1.0];
}

export class RelockEngine {
  constructor(manifest) {
    this.manifest = manifest;
    this.sessionPack = null;
    this.modelUrl = null;
    this.steererUrl = null;
    this.steererW = null;
    this.renderImg = null;
    this.renderNatW = RENDER_SIZE;
    this.renderNatH = RENDER_SIZE;
    this.lastScale = 0.5;
    this.renderCache = new Map();
    this.ep = null;
    this.lastTiming = null;
    this.gpuMnn = null;
    this.gpuMnnFailed = false;
  }

  setRenderImage(img) {
    this.renderImg = img;
    this.renderNatW = img.naturalWidth || img.videoWidth || img.width || RENDER_SIZE;
    this.renderNatH = img.naturalHeight || img.videoHeight || img.height || RENDER_SIZE;
    this.renderCache.clear();
  }

  async init() {
    const m = this.manifest.models || {};
    this.modelUrl = m.learned_rgb || m.rgb01 || m.learnedRgb || "models/xfeat_learn_steer_rgb01.onnx";
    this.steererUrl = m.steerer || "models/steerer.json";
    this.sessionPack = await createSession(this.modelUrl, { ep: this.manifest?.relock?.ortEp });
    this.ep = this.sessionPack.ep;
    await this.ensureLearnedSteerer();
    return this.ep;
  }

  async ensureLearnedSteerer() {
    if (this.steererW || !this.steererUrl) return;
    const sj = await (await fetch(this.steererUrl)).json();
    this.steererW = new Float32Array(sj.W.flat());
  }

  async warmupBase(onStatus) {
    const cfg = this.manifest.relock || {};
    const size = cfg.maxSideInitial ?? 1200;
    const topK = cfg.topKInitial ?? 4096;
    onStatus?.(`warming RGB learned render features (${size}px)…`);
    await this.getRenderFeatures(this.lastScale, size, topK);
  }

  preloadFallbacks(onStatus) {
    // No alternate model tiers in the parity path. Keep API for client compatibility.
    onStatus?.(`RGB learned relock ready (${this.ep || "?"})`);
  }

  async extract(proc, topK) {
    if (!this.sessionPack) await this.init();
    const { session, ort } = this.sessionPack;
    const tTotal = perfNow();
    let t = perfNow();
    const prepared = prepareBgr01ForNet(proc.bgr01, proc.W, proc.H);
    const prepareMs = perfNow() - t;
    const tensor = new ort.Tensor("float32", prepared.input, [1, 3, prepared.netH, prepared.netW]);
    postDiag(`relock-timing net start size=${proc.W}x${proc.H} net=${prepared.netW}x${prepared.netH} topK=${topK}`);
    t = perfNow();
    const out = await session.run({ image: tensor });
    const tNet = perfNow() - t;
    postDiag(`relock-timing net done ${tNet.toFixed(1)}ms size=${proc.W} topK=${topK}`);
    t = perfNow();
    const det = detectFromNetOutputs(out.feats.data, out.kheat.data, out.rel.data, prepared, topK);
    const detectMs = perfNow() - t;
    const timing = { prepare_ms: prepareMs, net_ms: tNet, detect_ms: detectMs, total_ms: perfNow() - tTotal, n: det.n, netW: prepared.netW, netH: prepared.netH };
    return { ...det, W: proc.W, H: proc.H, tNet, timing };
  }

  async getRenderFeatures(scale, size, topK) {
    const key = `${scale}:${size}:${topK}`;
    if (this.renderCache.has(key)) {
      const hit = this.renderCache.get(key);
      hit.cacheHit = true;
      return hit;
    }
    const tTotal = perfNow();
    const proc = preprocessSourceBgr01(this.renderImg, size, scale);
    const feat = await this.extract(proc, topK);
    // Matching only needs the render preprocessing transform after inference.
    // Do not retain the 17 MB BGR tensor and canvas for every searched scale.
    const { bgr01: _bgr01, canvas: _canvas, ...procMetadata } = proc;
    const entry = { feat, proc: procMetadata, scale, cacheHit: false, timing: { preprocess: proc.timing, extract: feat.timing, total_ms: perfNow() - tTotal } };
    this.renderCache.set(key, entry);
    return entry;
  }


  steeredDescs(feat, rot) {
    const r = ((Math.round(rot) % 4) + 4) % 4;
    if (r === 0) return feat.descs;
    if (!feat._steeredCache) feat._steeredCache = new Map();
    const key = `${this.steererW ? "learned" : "perm"}:${r}`;
    if (feat._steeredCache.has(key)) return feat._steeredCache.get(key);
    let out;
    if (this.steererW) {
      let start = 0;
      out = feat.descs;
      for (let k = r - 1; k >= 1; k--) {
        const prevKey = `learned:${k}`;
        if (feat._steeredCache.has(prevKey)) { start = k; out = feat._steeredCache.get(prevKey); break; }
      }
      for (let k = start; k < r; k++) out = applyLearnedSteer(out, feat.n, this.steererW);
    } else {
      out = applyPermSteer(feat.descs, feat.n, r);
    }
    feat._steeredCache.set(key, out);
    return out;
  }

  async gpuSteeredMatch(A, B, minCos, rotations = null) {
    if (this.gpuMnnFailed) return null;
    try {
      // The detector still returns Float32Array descriptors to JS. Packing those
      // back to fp16 on the CPU is slower than f32 upload in Chromium today; fp16
      // becomes the right default once descriptor sampling stays GPU-resident.
      if (!this.gpuMnn) this.gpuMnn = new GpuMnnMatcher({ preferF16: false });
      const tTotal = perfNow();
      const timing = { rotations: [], gpu: true };
      const rots = rotations?.length ? rotations : [0, 1, 2, 3];
      let best = null, bestRot = rots[0] ?? 0;
      for (const rRaw of rots) {
        const r = ((Math.round(rRaw) % 4) + 4) % 4;
        const tSteer = perfNow();
        const d0 = this.steeredDescs(A, r);
        const steerMs = perfNow() - tSteer;
        const tMnn = perfNow();
        const cand = await this.gpuMnn.match(d0, A.n, B.descs, B.n, minCos);
        const mnnMs = perfNow() - tMnn;
        timing.rotations.push({ rot: r, steer_ms: steerMs, mnn_ms: mnnMs, matches: cand.idx0.length, gpu: cand.timing });
        postDiag(`relock-timing match rotation rot=${r} steer=${steerMs.toFixed(1)}ms gpu_mnn=${mnnMs.toFixed(1)}ms matches=${cand.idx0.length} max=${cand.timing.max_score?.toFixed?.(3) ?? "na"}`);
        if (!best || cand.idx0.length > best.idx0.length) { best = cand; bestRot = r; }
      }
      timing.best_rot = bestRot;
      timing.total_ms = perfNow() - tTotal;
      return { idx0: best?.idx0 || [], idx1: best?.idx1 || [], rot: bestRot, timing };
    } catch (e) {
      this.gpuMnnFailed = true;
      postDiag(`relock-timing gpu-mnn unavailable (${e?.message || e}); using CPU MNN`);
      return null;
    }
  }

  async matchPair(renderEntry, frameFeat, frameProc, { minCos, imageSize, debugDump = false, rotations = null, ransacIters = 2000, ransacConfidence = 0.95 }) {
    const tTotal = perfNow();
    const A = renderEntry.feat;
    const B = frameFeat;
    const tMatch = perfNow();
    const m = await this.gpuSteeredMatch(A, B, minCos, rotations) || steeredMatch(A.descs, A.n, B.descs, B.n, minCos, this.steererW, rotations);
    const matchMs = perfNow() - tMatch;
    if (m.idx0.length < 4) {
      postDiag(`match-rgb: render=${A.W}×${A.H} kpts=${A.n} frame=${B.W}×${B.H} kpts=${B.n} steer=learned preRANSAC=${m.idx0.length} scale=${renderEntry.scale} match_ms=${matchMs.toFixed(1)} NO_H`);
      const timing = { match_ms: matchMs, match: m.timing, total_ms: perfNow() - tTotal };
      if (debugDump) return { _debugOnly: true, scale: renderEntry.scale, matches: m.idx0.length, rot: m.rot, noH: true, timing };
      return null;
    }
    const src = m.idx0.map((i) => [A.kpts[i * 2], A.kpts[i * 2 + 1]]);
    const dst = m.idx1.map((j) => [B.kpts[j * 2], B.kpts[j * 2 + 1]]);
    const tRansac = perfNow();
    const res = bestHomography(src, dst, {
      method: "magsac",
      threshs: [3],
      iters: ransacIters,
      confidence: ransacConfidence,
      imageSize: imageSize ?? frameProc.W,
    });
    const ransacMs = perfNow() - tRansac;
    const timing = { match_ms: matchMs, ransac_ms: ransacMs, match: m.timing, total_ms: perfNow() - tTotal };
    const diag = `match-rgb: render=${A.W}×${A.H} kpts=${A.n} frame=${B.W}×${B.H} kpts=${B.n} steer=learned rot=${m.rot} preRANSAC=${m.idx0.length} inliers=${res?.nInliers ?? 0} score=${res?.candScore?.toFixed?.(1) ?? "na"} scale=${renderEntry.scale} match=${matchMs.toFixed(1)}ms ransac=${ransacMs.toFixed(1)}ms`;
    postDiag(diag);
    if (!res) {
      if (debugDump) return { _debugOnly: true, scale: renderEntry.scale, matches: m.idx0.length, rot: m.rot, noH: true, timing };
      return null;
    }

    const Hproc = flat3(res.H);
    const invFrameM = mat3Inv(frameProc.M);
    const invRenderM = mat3Inv(renderEntry.proc.M);
    if (!invFrameM || !invRenderM) return null;
    const Hdisplay = mat3Mul(invFrameM, mat3Mul(Hproc, renderEntry.proc.M));

    const renderInlierProc = [];
    const frameInlierProc = [];
    for (let k = 0; k < m.idx0.length; k++) {
      if (!res.inliers[k]) continue;
      const i = m.idx0[k];
      const j = m.idx1[k];
      renderInlierProc.push([A.kpts[i * 2], A.kpts[i * 2 + 1]]);
      frameInlierProc.push([B.kpts[j * 2], B.kpts[j * 2 + 1]]);
    }

    const out = {
      H_display: Hdisplay,
      H_raw: Hdisplay,
      render_pts: projectWithH(invRenderM, renderInlierProc),
      frame_pts: projectWithH(invFrameM, frameInlierProc),
      inliers: res.nInliers,
      matches: m.idx0.length,
      score: res.candScore,
      steerRot: m.rot,
      spread: res.spread,
      scale: renderEntry.scale,
      tNet: frameFeat.tNet,
      timing,
    };
    if (debugDump) {
      out.debugDump = {
        scale: renderEntry.scale,
        matches: m.idx0.length,
        rot: m.rot,
        inliers: res.nInliers,
        score: res.candScore,
        spread: res.spread,
        H_display: Hdisplay,
        inlierIdx: res.inliers.map((v, i) => v ? i : -1).filter(i => i >= 0),
        src: src,
        dst: dst,
      };
    }
    return out;
  }

  releaseFrameGpuBuffers(feat) {
    try { if (this.gpuMnn && feat?.descs) this.gpuMnn.release(feat.descs); } catch {}
  }

  async searchScales(frameSource, scales, opts) {
    const tTotal = perfNow();
    const size = opts.maxSide ?? 1200;
    const topK = opts.topK ?? 4096;
    const minCos = opts.minCos ?? 0.8;
    const minScore = opts.minScore ?? -Infinity;
    const minInliers = opts.minInliers ?? -Infinity;
    const timing = { size, topK, minCos, minScore, minInliers, scales: [] };
    postDiag(`relock-timing frame preprocess start size=${size} topK=${topK}`);
    const frameProc = preprocessSourceBgr01(frameSource, size, 1.0);
    timing.frame_preprocess = frameProc.timing;
    postDiag(`relock-timing frame preprocess done ${frameProc.timing.total_ms.toFixed(1)}ms`);
    const frameFeat = await this.extract(frameProc, topK);
    timing.frame_extract = frameFeat.timing;
    let best = null;
    let bestAcceptable = null;
    const dumps = [];
    for (const sc of scales) {
      const scaleStart = perfNow();
      const cacheKey = `${sc}:${size}:${topK}`;
      const cacheHit = this.renderCache.has(cacheKey);
      postDiag(`relock-timing scale start scale=${sc} cache=${cacheHit ? "hit" : "miss"}`);
      const renderEntry = await this.getRenderFeatures(sc, size, topK);
      const cand = await this.matchPair(renderEntry, frameFeat, frameProc, { minCos, imageSize: size, debugDump: opts.debugDump, rotations: opts.rotations });
      const scaleTiming = {
        scale: sc,
        cache_hit: cacheHit,
        render: cacheHit ? null : renderEntry.timing,
        match: cand?.timing ?? null,
        matches: cand?.matches ?? null,
        inliers: cand?._debugOnly ? null : cand?.inliers ?? null,
        score: cand?._debugOnly ? null : cand?.score ?? null,
        accepted_gate: !!cand && !cand._debugOnly && cand.inliers >= minInliers && cand.score >= minScore,
        total_ms: perfNow() - scaleStart,
      };
      timing.scales.push(scaleTiming);
      postDiag(`relock-timing scale done scale=${sc} total=${scaleTiming.total_ms.toFixed(1)}ms in=${scaleTiming.inliers ?? "na"} score=${Number.isFinite(scaleTiming.score) ? scaleTiming.score.toFixed(1) : "na"}`);
      if (!cand) continue;
      if (opts.debugDump) dumps.push(cand.debugDump || cand);
      if (cand._debugOnly) continue;
      if (!best || cand.score > best.score) best = cand;
      if (cand.inliers >= minInliers && cand.score >= minScore) {
        if (!bestAcceptable || cand.score > bestAcceptable.score) bestAcceptable = cand;
        if (opts.stopOnAccept) {
          postDiag(`relock-timing early accept scale=${sc} in=${cand.inliers} score=${Number.isFinite(cand.score) ? cand.score.toFixed(1) : "na"}`);
          break;
        }
      }
    }
    if (opts.debugDump) {
      postDiag(`relock-dump ${JSON.stringify({ label: opts.debugLabel, size, topK, scales, candidates: dumps })}`);
    }
    this.releaseFrameGpuBuffers(frameFeat);
    // Important: do not let a high-score/low-inlier candidate mask a slightly
    // lower-score candidate that actually passes the acceptance gate. This was
    // the f810 browser miss: scale 0.65 had the highest score but 21 inliers,
    // while scale 0.30 had enough inliers and should have been considered.
    const selected = bestAcceptable || best;
    timing.total_ms = perfNow() - tTotal;
    timing.early_stop = !!opts.stopOnAccept && !!bestAcceptable;
    timing.selected = selected ? { scale: selected.scale, score: selected.score, inliers: selected.inliers, acceptable: selected === bestAcceptable } : null;
    postDiag(`relock-timing search done total=${timing.total_ms.toFixed(1)}ms selected=${timing.selected ? `${timing.selected.scale}/${timing.selected.inliers}/${timing.selected.score?.toFixed?.(1)}` : "none"}`);
    return { best: selected, timing };
  }

  async scanFrames(items, onStatus, options = {}) {
    if (!items?.length) return null;
    const cfg = this.manifest.relock || {};
    const tTotal = perfNow();
    const size = options.maxSide ?? 640;
    const topK = options.topK ?? 768;
    const minCos = options.minCos ?? cfg.minCos ?? 0.8;
    const minScore = options.minScore ?? 15;
    const minInliers = options.minInliers ?? 45;
    const rotations = options.rotations ?? [2];
    const scales = options.scales ?? [0.45, 0.5, 0.55];
    const newestFirst = options.newestFirst !== false;
    const stopOnAccept = options.stopOnAccept !== false;
    const ransacIters = options.ransacIters ?? 2000;
    const ransacConfidence = options.ransacConfidence ?? 0.95;
    const atlasTopK = Math.ceil(topK * items.length * (options.atlasTopKFactor ?? 1.25));
    const timing = { mode: "temporal-atlas", size, topK, atlasTopK, minCos, minScore, minInliers, rotations, scales, newestFirst, stopOnAccept, ransacIters, ransacConfidence, frames: [] };
    onStatus?.(`startup scan ${items.length} frames ${size}px topK=${topK}…`);
    postDiag(`startup-scan atlas start frames=${items.map(i => i.frame).join(",")} size=${size} topK=${topK} atlasTopK=${atlasTopK}`);
    const atlasProc = preprocessFrameAtlasBgr01(items, size, { gutter: options.atlasGutter ?? 96, cols: options.atlasCols ?? null });
    timing.atlas_preprocess = atlasProc.timing;
    const atlasFeat = await this.extract(atlasProc, atlasTopK);
    timing.atlas_extract = atlasFeat.timing;
    const frameFeats = splitAtlasFeatures(atlasFeat, atlasProc.tiles, topK, options.atlasGuard ?? 0);
    let best = null;
    let bestAcceptable = null;
    let stoppedEarly = false;
    const frameOrder = frameFeats.map((_, i) => i);
    if (newestFirst) frameOrder.reverse();
    const released = new Set();
    for (const fi of frameOrder) {
      const tile = atlasProc.tiles[fi];
      const frameFeat = frameFeats[fi];
      const frameProc = { W: tile.W, H: tile.H, M: tile.M, natW: tile.natW, natH: tile.natH };
      const ft = { frame: tile.frame, keypoints: frameFeat.n, scales: [] };
      for (const sc of scales) {
        const scaleStart = perfNow();
        const cacheKey = `${sc}:${size}:${topK}`;
        const cacheHit = this.renderCache.has(cacheKey);
        const renderEntry = await this.getRenderFeatures(sc, size, topK);
        const cand = await this.matchPair(renderEntry, frameFeat, frameProc, { minCos, imageSize: size, rotations, ransacIters, ransacConfidence });
        const accepted = !!cand && cand.inliers >= minInliers && cand.score >= minScore;
        ft.scales.push({
          scale: sc,
          cache_hit: cacheHit,
          render: cacheHit ? null : renderEntry.timing,
          match: cand?.timing ?? null,
          matches: cand?.matches ?? null,
          inliers: cand?.inliers ?? null,
          score: cand?.score ?? null,
          accepted_gate: accepted,
          total_ms: perfNow() - scaleStart,
        });
        if (cand) {
          cand.frame = tile.frame;
          cand.kind = "startup-scan";
          if (!best || cand.score > best.score) best = cand;
          if (accepted && (!bestAcceptable || cand.score > bestAcceptable.score)) {
            bestAcceptable = cand;
            if (stopOnAccept) {
              stoppedEarly = true;
              postDiag(`startup-scan early accept frame=${tile.frame} scale=${sc} in=${cand.inliers} score=${Number.isFinite(cand.score) ? cand.score.toFixed(1) : "na"}`);
              break;
            }
          }
        }
      }
      this.releaseFrameGpuBuffers(frameFeat);
      released.add(fi);
      timing.frames.push(ft);
      postDiag(`startup-scan frame=${tile.frame} kpts=${frameFeat.n} best=${ft.scales.map(s => `${s.scale}:${s.inliers ?? "na"}/${Number.isFinite(s.score) ? s.score.toFixed(1) : "na"}`).join(" ")}`);
      if (stoppedEarly) break;
    }
    for (let i = 0; i < frameFeats.length; i++) if (!released.has(i)) this.releaseFrameGpuBuffers(frameFeats[i]);
    const selected = bestAcceptable || null;
    timing.total_ms = perfNow() - tTotal;
    timing.accepted = !!selected;
    timing.early_stop = stoppedEarly;
    timing.selected = selected ? { frame: selected.frame, scale: selected.scale, score: selected.score, inliers: selected.inliers } : null;
    this.lastTiming = timing;
    postDiag(`startup-scan done total=${timing.total_ms.toFixed(1)}ms selected=${selected ? `${selected.frame}/${selected.scale}/${selected.inliers}/${selected.score?.toFixed?.(1)}` : "none"}`);
    if (selected) {
      selected.tier = "learned-rgb";
      selected.tierName = "startup-temporal-atlas";
      selected.timing = timing;
      this.lastScale = selected.scale;
    }
    return selected;
  }

  async lockFrameWithPose(frameSource, poseH, onStatus, options = {}) {
    const initial = !options.refresh && !options.localOnly && !options.fastOnly;
    if (!initial && poseH?.length === 9) {
      const tCanon = perfNow();
      const { canvas, H_canonical_to_frame } = canonicalCanvasFromFrame(frameSource, poseH, {
        targetBoardPx: options.targetBoardPx ?? 500,
        margin: options.margin ?? 0.4,
        renderSize: this.manifest?.canvas_size ?? RENDER_SIZE,
      });
      const canonicalMs = perfNow() - tCanon;
      postDiag(`relock-timing canonical crop done ${canonicalMs.toFixed(1)}ms`);
      onStatus?.("canon-local relock…");
      const r0 = await this.lockFrame(canvas, onStatus, { ...options, localOnly: true });
      this.lastTiming = { ...(this.lastTiming || {}), canonical_crop_ms: canonicalMs };
      if (!r0) return null;
      const out = transformRelockToFrame(r0, H_canonical_to_frame);
      out.timing = this.lastTiming;
      return out;
    }
    return this.lockFrame(frameSource, onStatus, options);
  }

  async lockFrame(frameSource, onStatus, options = {}) {
    const cfg = this.manifest.relock || {};
    const initial = !options.refresh && !options.localOnly && !options.fastOnly;
    const size = options.maxSide ?? (initial ? (cfg.maxSideInitial ?? 1200) : (cfg.maxSide ?? 640));
    const topK = options.topK ?? (initial ? (cfg.topKInitial ?? 4096) : (cfg.topKFull ?? 1536));
    const minCos = options.minCos ?? cfg.minCos ?? 0.8;
    const minScore = options.minScore ?? (initial ? 8 : (cfg.minScore ?? 8));
    const minInliers = options.minInliers ?? (initial ? 12 : (cfg.minInliers ?? 12));

    const scales = options.scales
      ? options.scales
      : options.localOnly
        ? localScales(this.lastScale, options.localRadius ?? 0.20)
        : fullScales();

    onStatus?.(`RGB relock ${size}px topK=${topK} scales=${scales.length}…`);
    postDiag(`lockFrame opts initial=${initial} size=${size} topK=${topK} scales=${JSON.stringify(scales)} source=${frameSource?.width || frameSource?.videoWidth || '?'}x${frameSource?.height || frameSource?.videoHeight || '?'}`);
    const { best, timing } = await this.searchScales(frameSource, scales, {
      maxSide: size,
      topK,
      minCos,
      minScore,
      minInliers,
      stopOnAccept: options.stopOnAccept,
      debugDump: options.debugDump,
      debugLabel: options.debugLabel,
      rotations: options.rotations,
    });
    timing.initial = initial;
    timing.localOnly = !!options.localOnly;
    timing.accepted = !!(best && best.inliers >= minInliers && best.score >= minScore);
    this.lastTiming = timing;
    if (best) {
      best.tier = "learned-rgb";
      best.tierName = initial ? "learned-rgb-full" : "learned-rgb-local";
      best.timing = timing;
    }
    if (best && best.inliers >= minInliers && best.score >= minScore) {
      this.lastScale = best.scale;
      return best;
    }
    if (best) onStatus?.(`no accept: best inliers=${best.inliers} score=${Number.isFinite(best.score) ? best.score.toFixed(1) : "?"} scale=${best.scale}`);
    return null;
  }

  static frameFromVideo(video) {
    const c = document.createElement("canvas");
    c.width = video.videoWidth;
    c.height = video.videoHeight;
    c.getContext("2d").drawImage(video, 0, 0);
    return c;
  }
}
